<template>
  <section class="subscription-box-order-return-detail" v-loading="loading">

    <el-breadcrumb>
      <el-breadcrumb-item :to="{ name: 'subscription-box-order-return-list' }">Retornos/Devoluções</el-breadcrumb-item>
      <el-breadcrumb-item><b>#{{ id }}</b></el-breadcrumb-item>
    </el-breadcrumb>

    <template v-if="orderReturn">

      <!-- What is blocking the return, and what to do about it -->
      <el-alert v-if="orderReturn.lastFailureReason" class="failure-alert" type="error" :closable="false" show-icon
        title="A devolução parou neste ponto">
        <div>{{ orderReturn.lastFailureReason }}</div>
        <small v-if="orderReturn.lastFailureAt">Última tentativa em {{ formatDate(orderReturn.lastFailureAt) }}</small>
        <div class="failure-hint">
          Tente novamente ou, se a nota precisar ser emitida na mão, emita no Bling e informe os dados abaixo.
        </div>
      </el-alert>

      <el-alert v-else-if="orderReturn.pending" class="failure-alert" type="warning" :closable="false" show-icon
        title="Devolução ainda não finalizada"
        description="Faltam etapas para concluir. Use 'Tentar novamente' ou informe as notas emitidas manualmente." />

      <el-alert v-else class="failure-alert" type="success" :closable="false" show-icon
        title="Devolução finalizada" description="Nada pendente para esta devolução." />

      <!-- Progress -->
      <el-steps class="steps" :active="activeStep" align-center finish-status="success">
        <el-step title="Registrada"></el-step>
        <el-step title="NF de devolução"></el-step>
        <el-step title="Finalizada"></el-step>
      </el-steps>

      <!-- Actions -->
      <div class="actions">
        <el-button v-if="orderReturn.pending" type="primary" icon="el-icon-refresh" :loading="working"
          @click="retry">Tentar novamente</el-button>

        <el-button v-if="canRegisterReturnNFe" icon="el-icon-document-add"
          @click="openNFeDialog('return')">Informar NF de devolução</el-button>

        <el-button v-if="canRegisterLossNFe" icon="el-icon-document-add"
          @click="openNFeDialog('loss')">Informar NF de perda</el-button>

        <el-button v-if="canDiscard" type="danger" plain icon="el-icon-delete"
          @click="discard">Descartar devolução</el-button>
      </div>

      <!-- Summary -->
      <el-descriptions class="summary" :column="3" border>
        <el-descriptions-item label="Pedido">
          <router-link :to="{ name: 'subscription-box-order-detail', params: { id: orderReturn.boxOrderId } }">
            {{ orderReturn.boxOrderId }}
          </router-link>
        </el-descriptions-item>
        <el-descriptions-item label="Cliente">{{ customerName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="Edição">{{ (order && order.edition) || '—' }}</el-descriptions-item>

        <el-descriptions-item label="Tipo">{{ describeType(orderReturn.type) }}</el-descriptions-item>
        <el-descriptions-item label="Status">
          <el-tag :type="statusTagType" size="small">{{ describeStatus(orderReturn.status) }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="NF do pedido">{{ (order && order.nfe) || '—' }}</el-descriptions-item>

        <el-descriptions-item label="NF de devolução">{{ formatNFe(orderReturn.returnNFe, orderReturn.returnNFeSeries) }}</el-descriptions-item>
        <el-descriptions-item label="NF de perda">{{ formatNFe(orderReturn.lossNFe, orderReturn.lossNFeSeries) }}</el-descriptions-item>
        <el-descriptions-item label="Registrada em">{{ formatDate(orderReturn.createdAt) }}</el-descriptions-item>
      </el-descriptions>

      <!-- Products -->
      <h2 class="table-label">Produtos devolvidos</h2>
      <el-table class="products-table" :data="returningProducts" size="mini">
        <el-table-column prop="id" label="ID" width="90" />
        <el-table-column prop="name" label="Produto" />
        <el-table-column label="Destino" width="160">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.usable" type="success" size="mini">Reaproveitado</el-tag>
            <el-tag v-else type="danger" size="mini">Descartado</el-tag>
          </template>
        </el-table-column>
      </el-table>

    </template>

    <!-- Manual NFe entry -->
    <el-dialog :title="nfeDialogTitle" :visible.sync="nfeDialogVisible" width="460px">
      <el-alert class="dialog-hint" type="info" :closable="false"
        title="Emita a nota no Bling antes de informar os dados aqui. A entrada de estoque no Bling também é feita manualmente." />

      <el-form ref="nfeForm" :model="nfeForm" :rules="nfeFormRules" label-width="110px">
        <el-form-item label="Número" prop="nfe">
          <el-input v-model="nfeForm.nfe" placeholder="Número da nota" />
        </el-form-item>
        <el-form-item label="Série" prop="nfeSeries">
          <el-input v-model="nfeForm.nfeSeries" placeholder="Série da nota" />
        </el-form-item>
        <el-form-item label="ID no Bling" prop="nfeBlingId">
          <el-input v-model="nfeForm.nfeBlingId" placeholder="Opcional" />
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="nfeDialogVisible = false" :disabled="working">Cancelar</el-button>
        <el-button type="primary" :loading="working" @click="submitNFe">Salvar e continuar</el-button>
      </span>
    </el-dialog>

  </section>
</template>

<script>
import moment from 'moment';

import CustomerService from '@/services/CustomerService';
import ProductService from '@/services/stock/ProductService';
import SubscriptionBoxOrderReturnService from '@/services/fulfillment/SubscriptionBoxOrderReturnService';
import SubscriptionBoxOrderService from '@/services/fulfillment/SubscriptionBoxOrderService';

export default {
  name: 'subscription-box-order-return-detail',
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      working: false,
      orderReturn: null,
      order: null,
      customerName: null,
      products: [],
      nfeDialogVisible: false,
      nfeDialogKind: 'return',
      nfeForm: {
        nfe: '',
        nfeSeries: '',
        nfeBlingId: '',
      },
      nfeFormRules: {
        nfe: [
          { required: true, message: 'Informe o número da nota', trigger: 'blur' },
        ],
      },
    };
  },
  computed: {
    activeStep() {
      if (!this.orderReturn) return 0;
      switch (this.orderReturn.status) {
        case 'FINALIZED': return 3;
        case 'RETURN_NFE_CREATED': return 2;
        default: return 1;
      }
    },
    statusTagType() {
      if (!this.orderReturn.pending) return 'success';
      return this.orderReturn.lastFailureReason ? 'danger' : 'warning';
    },
    discardedProductIds() {
      if (!this.orderReturn) return [];
      return this.orderReturn.returningProducts
        .filter(productId => this.orderReturn.usableProducts.indexOf(productId) === -1);
    },
    returningProducts() {
      if (!this.orderReturn) return [];
      return this.orderReturn.returningProducts.map(productId => ({
        id: productId,
        name: (this.products.find(product => product.id === productId) || {}).name || '—',
        usable: this.orderReturn.usableProducts.indexOf(productId) !== -1,
      }));
    },
    canRegisterReturnNFe() {
      return this.orderReturn.pending && !this.orderReturn.returnNFe && !this.orderReturn.returnNFeBlingId;
    },
    canRegisterLossNFe() {
      return this.orderReturn.pending
        && this.discardedProductIds.length > 0
        && !this.orderReturn.lossNFe
        && !this.orderReturn.lossNFeBlingId;
    },
    canDiscard() {
      return !this.orderReturn.returnNFe && !this.orderReturn.returnNFeBlingId
        && !this.orderReturn.lossNFe && !this.orderReturn.lossNFeBlingId;
    },
    nfeDialogTitle() {
      return this.nfeDialogKind === 'return' ? 'Informar NF de devolução' : 'Informar NF de perda';
    },
  },
  methods: {
    describeStatus(status) {
      return SubscriptionBoxOrderReturnService.describeStatus(status);
    },
    describeType(type) {
      return SubscriptionBoxOrderReturnService.describeType(type);
    },
    formatDate(iso) {
      if (!iso) return '—';
      return moment(iso).format('DD/MM/YYYY HH:mm');
    },
    formatNFe(nfe, series) {
      if (!nfe) return '—';
      return series ? `${nfe} / ${series}` : nfe;
    },
    async load() {
      this.loading = true;
      try {
        this.orderReturn = await SubscriptionBoxOrderReturnService.find(this.id);
        await Promise.all([this.loadOrder(), this.loadProducts()]);
      } catch (e) {
        this.$message.error('Falha ao carregar a devolução.');
      }
      this.loading = false;
    },
    async loadOrder() {
      try {
        this.order = await SubscriptionBoxOrderService
          .getSubscriptionBoxOrderById(this.orderReturn.boxOrderId);

        const customer = await CustomerService.getCustomerById(this.order.customerId);
        this.customerName = `${customer.firstName} ${customer.lastName}`;
      } catch (e) {
        // The order context is nice to have; a return still has to be fixable without it.
        this.order = null;
      }
    },
    async loadProducts() {
      try {
        this.products = await Promise.all(
          this.orderReturn.returningProducts.map(productId => ProductService.getProductById(productId)),
        );
      } catch (e) {
        this.products = [];
      }
    },
    // Replaces the loaded return with what the backend reports after an action and tells the
    // operator whether anything is still pending.
    applyResult(orderReturn, successMessage) {
      this.orderReturn = orderReturn;

      if (orderReturn.lastFailureReason) {
        this.$message.error(orderReturn.lastFailureReason);
      } else if (orderReturn.pending) {
        this.$message.warning('Devolução avançou, mas ainda não está finalizada.');
      } else {
        this.$message.success(successMessage);
      }
    },
    async retry() {
      this.working = true;
      try {
        this.applyResult(
          await SubscriptionBoxOrderReturnService.process(this.id),
          'Devolução finalizada.',
        );
      } catch (e) {
        this.$message.error(e.description || 'Falha ao processar a devolução.');
      }
      this.working = false;
    },
    openNFeDialog(kind) {
      this.nfeDialogKind = kind;
      this.nfeForm = { nfe: '', nfeSeries: '', nfeBlingId: '' };
      this.nfeDialogVisible = true;

      this.$nextTick(() => {
        if (this.$refs.nfeForm) this.$refs.nfeForm.clearValidate();
      });
    },
    async submitNFe() {
      try {
        await this.$refs.nfeForm.validate();
      } catch (e) {
        return;
      }

      const request = {
        nfe: this.nfeForm.nfe.trim(),
        nfeSeries: this.nfeForm.nfeSeries.trim() || null,
        nfeBlingId: this.nfeForm.nfeBlingId.trim() ? Number(this.nfeForm.nfeBlingId.trim()) : null,
      };

      this.working = true;
      try {
        const result = this.nfeDialogKind === 'return'
          ? await SubscriptionBoxOrderReturnService.registerReturnNFe(this.id, request)
          : await SubscriptionBoxOrderReturnService.registerLossNFe(this.id, request);

        this.nfeDialogVisible = false;
        this.applyResult(result, 'Nota informada e devolução finalizada.');
      } catch (e) {
        this.$message.error(e.description || 'Falha ao informar a nota.');
      }
      this.working = false;
    },
    async discard() {
      try {
        await this.$confirm(
          'A devolução será descartada e o pedido poderá ser registrado novamente. Nenhuma nota foi emitida para ela.',
          'Descartar devolução?',
          { confirmButtonText: 'Descartar', cancelButtonText: 'Cancelar', type: 'warning' },
        );
      } catch (e) {
        return;
      }

      this.working = true;
      try {
        await SubscriptionBoxOrderReturnService.remove(this.id);
        this.$message.success('Devolução descartada.');
        this.$router.push({ name: 'subscription-box-order-return-list' });
      } catch (e) {
        this.$message.error(e.description || 'Falha ao descartar a devolução.');
      }
      this.working = false;
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style lang="scss" scoped>
.subscription-box-order-return-detail {

  .el-breadcrumb {
    margin-bottom: 16px;
  }

  .failure-alert {
    margin-bottom: 16px;

    .failure-hint {
      margin-top: 8px;
    }
  }

  .steps {
    margin: 24px 0;
  }

  .actions {
    margin-bottom: 24px;
    text-align: center;
  }

  .table-label {
    font-size: 16px;
    margin-bottom: 8px;
    margin-top: 32px;
    text-align: left;
  }

  .dialog-hint {
    margin-bottom: 16px;
  }
}
</style>
